import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Music, Plus, Search, X, Play, Pause } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { loadSpotifyIframeApi } from "@/lib/spotifyEmbed";

// Preloads the Spotify embed API as soon as this section mounts (instead of on
// first click), so the first "play" press doesn't have to wait for the script to
// load before audio can start -- keeps it inside the same user-gesture window the
// browser's autoplay-with-sound policy expects.
function usePreloadedSpotifyApi() {
  const apiRef = useRef(null);
  useEffect(() => { loadSpotifyIframeApi().then((api) => { apiRef.current = api; }); }, []);
  return apiRef;
}

export default function SongRequests({ slug, wedding }) {
  const [songs, setSongs] = useState([]);
  const [nome, setNome] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // the chosen Spotify track, or null for free text
  const [showResults, setShowResults] = useState(false);
  const [sending, setSending] = useState(false);
  const [playingResultId, setPlayingResultId] = useState(null);
  const [playingSongId, setPlayingSongId] = useState(null);
  const boxRef = useRef(null);
  const apiRef = usePreloadedSpotifyApi();

  // Both previews use a real, visible Spotify embed rather than a hidden one --
  // see the long comment on the effects below for why: on iOS Safari a play()
  // sent across the cross-origin postMessage channel to the embed doesn't
  // reliably carry "this came from a user tap" credentials, so our own
  // programmatic play() can silently fail there even when called perfectly
  // synchronously. Showing the real widget means Spotify's own play button is
  // always available as a working fallback (a direct tap on their iframe is a
  // genuine gesture from their frame's point of view) -- our play() call is
  // just a convenience that happens to also autoplay it on desktop.
  const resultContainerRef = useRef(null);
  const resultControllerRef = useRef(null);
  const songContainerRef = useRef(null);
  const songControllerRef = useRef(null);

  useEffect(() => {
    if (!wedding.songs_enabled) return;
    api.get(`/public/${slug}/songs`).then((r) => setSongs(r.data.results)).catch(() => {});
  }, [slug, wedding.songs_enabled]);

  // Debounced live search against Spotify (via our own API, which holds the
  // credentials) -- picking a result attaches real track metadata; typing and
  // submitting without picking one still works as plain free text.
  useEffect(() => {
    if (!wedding.songs_enabled || selected || query.trim().length < 2) {
      setResults([]);
      return;
    }
    const id = setTimeout(() => {
      api.get(`/public/${slug}/songs/search`, { params: { q: query } })
        .then((r) => { setResults(r.data.results); setShowResults(true); })
        .catch(() => setResults([]));
    }, 350);
    return () => clearTimeout(id);
  }, [query, selected, slug, wedding.songs_enabled]);

  useEffect(() => {
    if (!showResults) return;
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setShowResults(false); };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [showResults]);

  const toggleResultPreview = (track) => {
    setPlayingResultId((id) => (id === track.spotify_id ? null : track.spotify_id));
  };

  // Closes the result preview widget (e.g. when the dropdown closes or a track
  // is picked) -- no separate pause call needed, destroying the controller
  // below (on playingResultId becoming null) stops playback with it.
  const stopHiddenPreview = () => setPlayingResultId(null);

  // Opens a real, visible embed for whichever search result is currently
  // toggled on, attempting autoplay (works on desktop; on iOS it just means
  // the widget appears and the visitor taps Spotify's own play button).
  useEffect(() => {
    resultControllerRef.current?.destroy?.();
    resultControllerRef.current = null;
    if (!playingResultId || !resultContainerRef.current) return;
    let cancelled = false;
    (async () => {
      const IFrameAPI = apiRef.current || (await loadSpotifyIframeApi());
      if (cancelled || !resultContainerRef.current) return;
      IFrameAPI.createController(
        resultContainerRef.current,
        { uri: `spotify:track:${playingResultId}`, width: "100%", height: "80" },
        (controller) => {
          if (cancelled) return;
          resultControllerRef.current = controller;
          controller.addListener("ready", () => controller.play());
        },
      );
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playingResultId]);

  // Opens the visible, autoplaying embed for a submitted song (or closes it).
  useEffect(() => {
    songControllerRef.current?.destroy?.();
    songControllerRef.current = null;
    if (!playingSongId) return;
    const song = songs.find((s) => s.id === playingSongId);
    if (!song?.spotify_id || !songContainerRef.current) return;
    let cancelled = false;
    (async () => {
      const IFrameAPI = apiRef.current || (await loadSpotifyIframeApi());
      if (cancelled || !songContainerRef.current) return;
      IFrameAPI.createController(
        songContainerRef.current,
        { uri: `spotify:track:${song.spotify_id}`, width: "100%", height: "80" },
        (controller) => {
          if (cancelled) return;
          songControllerRef.current = controller;
          controller.addListener("ready", () => controller.play());
        },
      );
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playingSongId]);

  if (!wedding.songs_enabled) return null;

  const pick = (track) => {
    stopHiddenPreview();
    setSelected(track);
    setQuery(`${track.titolo} — ${track.artista}`);
    setShowResults(false);
  };

  const clearSelection = () => {
    setSelected(null);
    setQuery("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return toast.error("Inserisci il titolo del brano");
    setSending(true);
    try {
      const payload = selected
        ? { nome: nome.trim(), canzone: selected.titolo, artista: selected.artista, spotify_id: selected.spotify_id, spotify_url: selected.spotify_url, album_image_url: selected.album_image_url }
        : { nome: nome.trim(), canzone: query.trim() };
      const { data } = await api.post(`/public/${slug}/songs`, payload);
      setSongs((s) => [data, ...s]);
      clearSelection();
      toast.success("Richiesta inviata, grazie!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="py-16 sm:py-20 px-6 sm:px-12 bg-white" data-testid="song-requests-section">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <Music size={28} className="mx-auto mb-4" style={{ color: "var(--wed-primary)" }} />
          <p className="eyebrow mb-3">Playlist di nozze</p>
          <h2 className="font-wed text-3xl sm:text-4xl">Che canzone non può mancare?</h2>
          <p className="text-neutral-500 mt-2 text-sm">Cerca il brano su Spotify, ascolta l'anteprima e invialo.</p>
        </div>
        <form onSubmit={submit} className="mb-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-[2] min-w-0" ref={boxRef}>
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
              <input
                className="input-wed !pl-9 w-full"
                placeholder="Titolo e artista"
                value={query}
                onChange={(e) => { setQuery(e.target.value); if (selected) setSelected(null); }}
                onFocus={() => results.length > 0 && setShowResults(true)}
                data-testid="song-request-title-input"
              />
              {selected && (
                <button type="button" onClick={clearSelection} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700" data-testid="song-request-clear-selection">
                  <X size={15} />
                </button>
              )}
              {showResults && results.length > 0 && (
                <div className="absolute left-0 right-0 mt-1.5 bg-white border rounded-xl shadow-lg overflow-hidden z-20 max-h-96 overflow-y-auto" style={{ borderColor: "#e3ddd2" }} data-testid="song-search-results">
                  {results.map((t) => (
                    <div key={t.spotify_id} className="border-b last:border-b-0" style={{ borderColor: "#f0ece1" }}>
                      <div className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-neutral-50 transition-colors duration-150">
                        <button type="button" onClick={() => pick(t)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`song-search-result-${t.spotify_id}`}>
                          {t.album_image_url ? (
                            <img src={t.album_image_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-neutral-100 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{t.titolo}</p>
                            <p className="text-xs text-neutral-400 truncate">{t.artista}</p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleResultPreview(t); }}
                          className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-150"
                          style={playingResultId === t.spotify_id ? { background: "var(--wed-primary)", color: "white" } : { background: "#f5f3ec", color: "#525252" }}
                          data-testid={`song-search-preview-toggle-${t.spotify_id}`}
                        >
                          {playingResultId === t.spotify_id ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" />}
                        </button>
                      </div>
                      {playingResultId === t.spotify_id && (
                        <div className="px-3 pb-2.5" onClick={(e) => e.stopPropagation()}>
                          <div ref={resultContainerRef} data-testid={`song-search-preview-embed-${t.spotify_id}`} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <input className="input-wed flex-1 min-w-0" placeholder="Il tuo nome" value={nome} onChange={(e) => setNome(e.target.value)} data-testid="song-request-name-input" />
            <button type="submit" disabled={sending} className="btn-wed !px-5 flex items-center justify-center gap-2 shrink-0" data-testid="song-request-submit">
              <Plus size={16} /> Invia
            </button>
          </div>
        </form>
        {songs.length > 0 && (
          <div className="space-y-2" data-testid="song-requests-list">
            {songs.map((s) => (
              <div key={s.id} className="bg-neutral-50 border rounded-xl" style={{ borderColor: "#e3ddd2" }}>
                <div className="flex items-center gap-2.5 p-2.5">
                  {s.album_image_url ? (
                    <img src={s.album_image_url} alt="" className="w-9 h-9 rounded object-cover shrink-0" />
                  ) : (
                    <span className="text-base shrink-0">🎵</span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{s.canzone}</p>
                    <p className="text-[0.65rem] text-neutral-400 truncate">{s.artista || ""}{s.nome ? ` · ${s.nome}` : ""}</p>
                  </div>
                  {s.spotify_id && (
                    <button
                      type="button"
                      onClick={() => setPlayingSongId((id) => (id === s.id ? null : s.id))}
                      className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors duration-150"
                      style={playingSongId === s.id ? { background: "var(--wed-primary)", color: "white" } : { background: "white", color: "#525252", border: "1px solid #e3ddd2" }}
                      data-testid={`song-preview-toggle-${s.id}`}
                    >
                      {playingSongId === s.id ? <Pause size={11} fill="currentColor" /> : <Play size={11} fill="currentColor" />}
                    </button>
                  )}
                </div>
                {playingSongId === s.id && (
                  <div className="px-2.5 pb-2.5">
                    <div ref={songContainerRef} data-testid={`song-embed-${s.id}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
